/**
 * Static legal + info pages.
 *
 * Kept in one file so the five routes (/about, /contact, /faq,
 * /privacy, /terms) share one LegalShell wrapper. Content is
 * placeholder-but-usable copy — the Master Admin can replace sections
 * in a future CMS pass.
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, MessageCircle } from "lucide-react";
import { Card, CardBody } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";

function LegalShell({
  title,
  lede,
  children,
  testid,
}: {
  title: string;
  lede?: string;
  children: React.ReactNode;
  testid: string;
}) {
  return (
    <div className="mx-auto max-w-3xl px-4 md:px-8 py-8 md:py-14" data-testid={testid}>
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-muted hover:text-ink mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back home
      </Link>
      <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">{title}</h1>
      {lede && <p className="text-ink-muted text-lg mb-6">{lede}</p>}
      <Card elevated>
        <CardBody className="prose prose-lg max-w-none text-ink [&_h2]:text-2xl [&_h2]:font-black [&_h2]:mt-6 [&_h2]:mb-2 [&_p]:mb-3 [&_p]:text-ink [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_a]:underline [&_a]:text-brand">
          {children}
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// About
// ---------------------------------------------------------------------------
export function About() {
  return (
    <LegalShell
      title="About Parts Buoy"
      lede="A niche marketplace + community forum for the people who actually keep boats running."
      testid="page-about"
    >
      <h2>Why Parts Buoy exists</h2>
      <p>
        Finding genuine boat parts — especially for older outboards and inboards —
        shouldn't require four tabs, two forums, and a long-distance phone call.
        Parts Buoy merges a searchable marketplace with an active community forum
        so questions, answers, and purchases live in one place.
      </p>

      <h2>How it works</h2>
      <ul>
        <li>
          <strong>Participants</strong> buy, ask questions, and occasionally sell
          spare parts. Commission is {(10).toFixed(0)}% on anything sold.
        </li>
        <li>
          <strong>Dealers</strong> pay a monthly subscription in exchange for a
          lower {(5).toFixed(0)}% commission, featured-dealer rotation, and a
          public storefront page.
        </li>
        <li>
          <strong>Admins</strong> (that's us) keep the garden free of spam and
          mediate the rare dispute.
        </li>
      </ul>

      <h2>Built with</h2>
      <p>
        Python/FastAPI + MongoDB for data, React + Tailwind for UI, Stripe for
        payments. Accessible, senior-friendly, and fast enough that browsing on
        a dock Wi-Fi doesn't feel like a chore.
      </p>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------
export function Contact() {
  return (
    <LegalShell
      title="Contact us"
      lede="Support, partnerships, press — we read everything."
      testid="page-contact"
    >
      <h2>Support</h2>
      <p>
        Got an order issue, a refund question, or a suspicious listing to report?
        Email <a href="mailto:PartsBuoy@gmail.com">PartsBuoy@gmail.com</a>.
        We answer within two business days.
      </p>

      <h2>Dealer onboarding</h2>
      <p>
        Selling volume and want to talk? Email{" "}
        <a href="mailto:PartsBuoy@gmail.com">PartsBuoy@gmail.com</a>. We'll
        walk you through storefront setup.
      </p>

      <h2>Press & partnerships</h2>
      <p>
        Email <a href="mailto:PartsBuoy@gmail.com">PartsBuoy@gmail.com</a>.
      </p>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <a href="mailto:PartsBuoy@gmail.com" className="w-full sm:w-auto">
          <Button variant="primary" size="md" fullWidth leadingIcon={<Mail className="h-4 w-4" />}>
            Email support
          </Button>
        </a>
        <Link to="/faq" className="w-full sm:w-auto">
          <Button variant="outline" size="md" fullWidth leadingIcon={<MessageCircle className="h-4 w-4" />}>
            Check the FAQ
          </Button>
        </Link>
      </div>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------
const FAQS: { q: string; a: React.ReactNode }[] = [
  {
    q: "How does commission work?",
    a: "When a buyer pays, Stripe sends the full amount to Parts Buoy. We deduct the platform commission (10% participant, 5% dealer) from the seller's payout — buyers never pay the fee directly.",
  },
  {
    q: "Can I sell from the forum?",
    a: "Yes. Our 'Reply-to-Sell' flow lets any reply attach a new product listing. Thread sorting then prioritises the cheapest dealer offer so buyers find the best price first.",
  },
  {
    q: "What if a part doesn't arrive?",
    a: "Open a dispute from your Orders tab. An admin can issue a full or partial Stripe refund within minutes.",
  },
  {
    q: "Why no third-party analytics?",
    a: "We run essential session cookies only — no tracking, no ad pixels. Keeps the site snappy and respectful of your data.",
  },
];

export function FAQ() {
  return (
    <LegalShell
      title="Frequently asked questions"
      lede="Short answers. If we missed yours, ping PartsBuoy@gmail.com."
      testid="page-faq"
    >
      {FAQS.map((item, i) => (
        <details
          key={item.q}
          className="group rounded-2xl border border-surface-border bg-surface-sunken px-5 py-4 mb-3"
          data-testid={`faq-item-${i}`}
        >
          <summary className="cursor-pointer font-bold text-ink list-none flex items-center justify-between">
            {item.q}
            <span className="text-brand text-2xl transition-transform group-open:rotate-45" aria-hidden>
              +
            </span>
          </summary>
          <div className="mt-2 text-ink">{item.a}</div>
        </details>
      ))}
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Privacy
// ---------------------------------------------------------------------------
export function Privacy() {
  return (
    <LegalShell
      title="Privacy policy"
      lede="Last updated: 2026. Short version: we keep what we need to run the marketplace, nothing more."
      testid="page-privacy"
    >
      <h2>What we collect</h2>
      <ul>
        <li>Account email, username, and (optional) full name.</li>
        <li>Your listings, forum posts, and orders.</li>
        <li>Shipping addresses when you buy a part.</li>
        <li>Stripe customer and subscription IDs — never your card data.</li>
      </ul>

      <h2>What we don't collect</h2>
      <ul>
        <li>Ad tracking cookies. None.</li>
        <li>Third-party analytics scripts.</li>
        <li>Your location beyond the shipping address you type in.</li>
      </ul>

      <h2>Cookies we use</h2>
      <ul>
        <li>
          <code>access_token</code> / <code>refresh_token</code> — HttpOnly auth
          cookies, expire when you log out.
        </li>
        <li>
          <code>pb-cookie-consent</code> — remembers that you dismissed the
          cookie banner.
        </li>
      </ul>

      <h2>Your rights</h2>
      <p>
        You can delete your account at any time from the Profile tab. We remove
        your personal data within 30 days and keep only anonymised order records
        for tax compliance.
      </p>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Terms of service
// ---------------------------------------------------------------------------
export function Terms() {
  return (
    <LegalShell
      title="Terms of service"
      lede="The short version: list honestly, ship promptly, don't sell counterfeits."
      testid="page-terms"
    >
      <h2>1. Account</h2>
      <p>
        You must be 18+ to create an account. You're responsible for everything
        posted or sold under your handle.
      </p>

      <h2>2. Listings</h2>
      <p>
        Listings must be accurate, legal to sell in the United States, and not
        infringe any third-party IP. We reserve the right to remove anything
        that violates these rules.
      </p>

      <h2>3. Payments</h2>
      <p>
        Stripe processes every transaction. Parts Buoy retains a commission on
        every successful sale (currently 10% for Participants, 5% for Dealers).
        Fees are subject to change with 30 days' notice; existing orders are
        always honoured at the fee captured at purchase time.
      </p>

      <h2>4. Refunds</h2>
      <p>
        Sellers set their own return policy. Admins may issue refunds at their
        discretion when a buyer proves a part was misrepresented, never shipped,
        or damaged in transit.
      </p>

      <h2>5. Community conduct</h2>
      <p>
        Be civil. Spam, scams, counterfeits, and harassment lead to account
        removal.
      </p>

      <h2>6. Termination</h2>
      <p>
        Either party can end this agreement at any time. Outstanding orders are
        processed in full before accounts are wound down.
      </p>
    </LegalShell>
  );
}
